import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem as MuiMenuItem,
  Link as MuiLink,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { linksAPI, peopleAPI, videosAPI } from '../services/api';

interface LinkItem {
  id: number;
  person_id: number;
  video_id: number;
  token: string;
  created_at: string;
  person_name?: string;
  video_title?: string;
  video_slug?: string;
}

interface Person {
  id: number;
  name: string;
}

interface Video {
  id: number;
  title: string;
  slug: string;
}

interface LinkForm {
  person_id: number;
  video_id: number;
}

const LinksPage = () => {
  const [links, setLinks] = useState<LinkItem[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<LinkForm>({
    person_id: 0,
    video_id: 0,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [peopleRes, videosRes, linksRes] = await Promise.all([
        peopleAPI.getAll(),
        videosAPI.getAll(),
        linksAPI.getAll(),
      ]);

      setLinks(linksRes.data);
      setPeople(peopleRes.data);
      setVideos(videosRes.data);
    } catch (error) {
      console.error('Veriler alınırken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setForm({
      person_id: 0,
      video_id: 0,
    });
    setFormErrors({});
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setForm({ person_id: 0, video_id: 0 });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.person_id) {
      errors.person_id = 'Kişi seçmelisiniz';
    }

    if (!form.video_id) {
      errors.video_id = 'Video seçmelisiniz';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const response = await linksAPI.create(form);

      await fetchData(); // Listeyi yenile
      handleCloseDialog();
      alert('Link başarıyla oluşturuldu');
    } catch (error: any) {
      console.error('Link oluşturulurken hata:', error);
      if (error.response?.data?.error) {
        if (error.response.data.error.includes('kişi')) {
          setFormErrors({ person_id: error.response.data.error });
        } else if (error.response.data.error.includes('video')) {
          setFormErrors({ video_id: error.response.data.error });
        } else {
          alert(error.response.data.error);
        }
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Bu linki silmek istediğinizden emin misiniz?')) {
      try {
        await linksAPI.delete(id);
        setLinks(links.filter(link => link.id !== id));
      } catch (error) {
        console.error('Link silinirken hata:', error);
      }
    }
  };

  const handleCopyLink = (videoSlug: string, token: string) => {
    const url = `https://video.banasesver.com/watch/${videoSlug}?t=${token}`;
    navigator.clipboard.writeText(url);
    alert('Link kopyalandı: ' + url);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Linkler
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Yeni Link
        </Button>
        <IconButton onClick={fetchData} sx={{ ml: 1 }}>
          <RefreshIcon />
        </IconButton>
      </Box>

      <Paper elevation={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kişi</TableCell>
                <TableCell>Video</TableCell>
                <TableCell>Token</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Oluşturulma</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {links.map((link) => (
                <TableRow key={link.id}>
                  <TableCell>{link.person_name || `ID: ${link.person_id}`}</TableCell>
                  <TableCell>{link.video_title || `ID: ${link.video_id}`}</TableCell>
                  <TableCell>
                    <Chip label={link.token.substring(0, 10) + '...'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <MuiLink
                      component="button"
                      variant="body2"
                      onClick={() => handleCopyLink(link.video_slug || 'video', link.token)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {`/watch/${link.video_slug}?t=${link.token.substring(0, 10)}...`}
                    </MuiLink>
                  </TableCell>
                  <TableCell>
                    {new Date(link.created_at).toLocaleDateString('tr-TR')}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleCopyLink(link.video_slug || 'video', link.token)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDelete(link.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {links.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      Henüz link oluşturulmamış. Yeni link oluşturmak için butona tıklayın.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Link Oluştur</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <FormControl fullWidth margin="normal" error={!!formErrors.person_id}>
              <InputLabel id="person-select-label">Kişi</InputLabel>
              <Select
                labelId="person-select-label"
                value={form.person_id}
                label="Kişi"
                onChange={(e: any) => setForm({ ...form, person_id: Number(e.target.value) })}
              >
                {people.map((person) => (
                  <MuiMenuItem key={person.id} value={person.id}>
                    {person.name}
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" error={!!formErrors.video_id}>
              <InputLabel id="video-select-label">Video</InputLabel>
              <Select
                labelId="video-select-label"
                value={form.video_id}
                label="Video"
                onChange={(e: any) => setForm({ ...form, video_id: Number(e.target.value) })}
              >
                {videos.map((video) => (
                  <MuiMenuItem key={video.id} value={video.id}>
                    {video.title}
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} variant="contained">
            Oluştur
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinksPage;